// pages/stations/RetailDashboardPage.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RetailDashboard from "@/pages/RetailDashboard";
import { useLoginChimeOnMount } from "@/app/chimes";
import { clearSession, confirmSession, getSession, http, useOfflineSessionUpgrade, useTheme } from "@/app/core";
import { beginLogoutSuppression, exitFullscreenIfActive } from "@/app/fullscreen";
import { useAutoLogoutOnHidden } from "@/app/fragments";


// ─────────────────────────────────────────────────────────────────────
// Session 30 — Retail POS dashboard wrapper. Session boot / confirm /
// logout live here; the POS itself is a self-contained page component
// (src/pages/RetailDashboard.jsx) to keep this monolith from growing.
// ─────────────────────────────────────────────────────────────────────
export function RetailDashboardPage() {
  const navigate = useNavigate();
  useLoginChimeOnMount();
  const [session, setSessionState] = useState(() => getSession("retail"));
  useOfflineSessionUpgrade("retail", session, setSessionState); // Session 46
  useEffect(() => { try { confirmSession("retail"); } catch (_) { /* noop */ } }, []);
  useEffect(() => {
    if (!session?.token) navigate("/retail", { replace: true });
  }, [session, navigate]);

  const logout = async () => {
    beginLogoutSuppression();
    await exitFullscreenIfActive();
    const tokenSnapshot = session?.token;
    clearSession("retail", "user_logout");
    setSessionState(null);
    navigate("/retail", { replace: true });
    try {
      if (tokenSnapshot) {
        await http.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${tokenSnapshot}` } });
      }
    } catch {
      // no-op — local session already cleared.
    }
  };

  useAutoLogoutOnHidden(logout, { enabled: !!session?.token, graceMs: 60000 });

  // Session 30.3 — theme control passed down so the standalone Retail
  // dashboard gets a light/dark toggle without importing from App.js.
  const { resolvedTheme, setTheme } = useTheme();
  const toggleTheme = () => setTheme(resolvedTheme === "dark" ? "light" : "dark");

  if (!session?.token) return null;
  return <RetailDashboard session={session} onLogout={logout} theme={resolvedTheme} onToggleTheme={toggleTheme} />;
}
